import { LocalizationProvider, DatePicker, DatePickerProps } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Control, Controller, FieldError, Path } from 'react-hook-form';
import { usePhrase } from '@/hooks/general/usePhrase/usePhrase';
import { format, formatISO, isValid, Locale, parse, parseISO } from 'date-fns';
import { enUS, enGB, de, fr, es, it, nl, da, fi, nb, sv, pt } from 'date-fns/locale';
import { JSXFromString } from '@/utils/JSXFromString';
import { ReactNode } from 'react';

interface CustomDatePickerFieldProps<T extends object> {
    control: Control<T>;
    name: Path<T>;
    label: number | string;
    error?: FieldError | null;
    hideError?: boolean;
    fullWidth?: boolean;
    required?: boolean;
    locale?: string;
    outputFormat?: 'locale' | 'iso';
}

type DatePickerFieldProps<T extends object> = CustomDatePickerFieldProps<T> &
    Omit<DatePickerProps, 'error' | 'label' | 'name'>;

export const datePickerLocaleMap: { [key: string]: Locale } = {
    es: es,
    pt: pt,
    it: it,
    de: de,
    fr: fr,
    nl: nl,
    da: da,
    fi: fi,
    no: nb,
    sv: sv,
};

const getLocale = (passedLocale?: string): Locale => {
    const locale = (passedLocale || navigator?.language)?.toLowerCase();
    const USLocalesList = ['en-us', 'en-ca'];

    if (!locale) {
        return enGB;
    }

    if (USLocalesList.includes(locale)) {
        return enUS;
    }

    const twoCharLocale = locale.slice(0, 2);
    return datePickerLocaleMap[twoCharLocale] || enGB;
};

const parseDate = (value: unknown, locale: Locale): Date | null => {
    if (value instanceof Date && isValid(value)) {
        return value;
    }
    if (typeof value === 'string') {
        // Try parsing with the locale's date format
        const parsedDate = parse(value, 'P', new Date(), { locale });
        if (isValid(parsedDate)) {
            return parsedDate;
        }
        // Fallback to ISO format if the parsing fails
        const isoDate = parseISO(value);
        if (isValid(isoDate)) {
            return isoDate;
        }
    }
    return null;
};

export const DatePickerField = <T extends object>(props: DatePickerFieldProps<T>) => {
    const { t } = usePhrase();
    const {
        control,
        name,
        label,
        error,
        hideError = false,
        fullWidth = false,
        required = false,
        locale,
        outputFormat = 'locale',
        ...rest
    } = props;

    const dateLocale = getLocale(locale);

    const getErrorText = (fieldError: FieldError | undefined) => {
        if (hideError) {
            return '';
        }
        const message = fieldError?.message || error?.message;
        if (message) {
            if (typeof message === 'number') {
                return t(message);
            } else {
                return JSXFromString(message) as ReactNode;
            }
        }
        return undefined;
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
            <Controller
                name={name}
                control={control}
                render={({ field, fieldState: { error: fieldError } }) => {
                    const parsedDate = parseDate(field.value, dateLocale);

                    return (
                        <DatePicker
                            {...rest}
                            {...field}
                            label={typeof label === 'string' ? label : t(label)}
                            value={parsedDate}
                            onChange={(date) => {
                                if (date && isValid(date)) {
                                    if (outputFormat === 'iso') {
                                        field.onChange(formatISO(date, { representation: 'date' }));
                                    } else {
                                        field.onChange(format(date, 'P', { locale: dateLocale }));
                                    }
                                } else {
                                    field.onChange(null);
                                }
                            }}
                            slotProps={{
                                textField: {
                                    fullWidth,
                                    required,
                                    error: Boolean(error || fieldError),
                                    helperText: getErrorText(fieldError),
                                    inputProps: { 'data-testid': `${name}-date-input` },
                                },
                            }}
                        />
                    );
                }}
            />
        </LocalizationProvider>
    );
};
